import { Scope } from 'wdc-cube';
import { MEASUREMENT_SOURCES, STATUS_TONES, STATUS_LABELS, formatMeasurement, measurementLabelOf, measurementTypeOf } from '../../../domain';
import { reuseRows } from '../../../components/rows';
import { TableModel } from '../../../components/table';
import { MEASUREMENT_COLUMNS } from '../../concrete/restricted/collaborators/collaborator-table';
import { HistoryCellScope, HistoryColumnScope, HistoryRowScope } from './patient-detail.scope';
/**
 * As duas tabelas de historico do detalhe do colaborador.
 *
 * Uma lista os DIAS (`tableHistoryDays.tsx`), outra as leituras de UM dia
 * (`tableHistory.tsx`). Sao a mesma tabela com colunas diferentes: a dos dias tem
 * "Dispositivo de Origem" e "Sono", a do dia nao tem nenhuma das duas.
 *
 * Montadas fora do presenter, como a tabela de colaboradores: e formatacao, e nao
 * ciclo de vida.
 */
/** As colunas da lista de DIAS, na ordem da referencia. */
export const DAY_FIELDS = [
    { field: 'date', label: 'Última atualização' },
    { field: 'origem', label: 'Dispositivo de Origem' },
    ...MEASUREMENT_COLUMNS.map((typeId) => ({ field: String(typeId), label: measurementLabelOf(typeId) }))
];
/**
 * As colunas das leituras de um dia.
 *
 * Sem "Dispositivo de Origem" e sem "Sono" - a referencia nao as tem nesta tabela.
 * Sono e uma medida do dia inteiro; dentro do dia ela nao diz nada.
 */
export const READING_FIELDS = [
    { field: 'date', label: 'Hora Monitoramento' },
    ...MEASUREMENT_COLUMNS.filter((typeId) => typeId !== 204).map((typeId) => ({
        field: String(typeId),
        label: measurementLabelOf(typeId)
    }))
];
/** A ordem em que as duas abrem: do mais recente para o mais antigo. */
export const DEFAULT_HISTORY_SORT = '-date';
/**
 * Os nomes de coluna que cada tabela sabe ordenar - a lista branca do `sort` que
 * chega pela URL.
 *
 * O detalhe alterna entre as duas com o MESMO parametro (`hist-ordem`), entao ele
 * aceita a uniao; o historico compartilhado so tem a de leituras.
 */
/**
 * O que a tabela de leituras sabe ordenar: a HORA e cada coluna de medicao.
 *
 * Os ids de tipo tinham saido em 16/09/2026, com o agrupamento por hora, e voltaram
 * em 08/10/2026: no historico compartilhado quem ordena e o SERVIDOR, pelo valor que
 * a celula mostra (soma nos tipos que acumulam, media nos demais), com as horas sem
 * aquele tipo no fim. Esta lista e a lista branca do `sort` que chega pela URL.
 */
export const READING_SORT_FIELDS = READING_FIELDS.map((column) => column.field);
export const HISTORY_SORT_FIELDS = [
    ...new Set([...DAY_FIELDS.map((column) => column.field), ...READING_SORT_FIELDS])
];
/**
 * Os cabecalhos, na ordem de `fields`.
 *
 * @param onDrop quando vem, as colunas depois da primeira passam a ser arrastaveis:
 *        e chamado com o campo arrastado e o campo sobre o qual ele foi solto
 */
export function buildHistoryColumns(fields, sort, update, onSort, onDrop, 
/** As colunas que ja estao na tela: a mesma coluna e o mesmo scope, atualizado. */
reuse) {
    const current = sort ?? DEFAULT_HISTORY_SORT;
    const descending = current.startsWith('-');
    const sorted = descending ? current.slice(1) : current;
    return reuseRows(reuse, fields, (column) => column.field, (column, reused) => {
        const index = fields.indexOf(column);
        const scope = reused ?? new HistoryColumnScope();
        scope.identity = column.field;
        scope.field = column.field;
        scope.label = column.label;
        scope.sorted = column.field === sorted;
        scope.descending = scope.sorted && descending;
        scope.onSort = () => onSort(column.field);
        // Atribuido nos dois casos: o scope reaproveitado pode ter sido movel antes.
        scope.movable = !!onDrop && index > 0;
        scope.onDrop = scope.movable ? (source) => onDrop(source, column.field) : async () => undefined;
        scope.update = update;
        return scope;
    });
}
/**
 * Uma linha da lista de dias: a data abre o dia, e as medicoes sao do dia todo.
 *
 * @param fields as colunas, na ordem em que a tabela as mostra - a primeira e a data
 */
export function buildDayRow(entry, update, onOpen, fields = DAY_FIELDS, reuse) {
    // A linha que ja esta na tela, quando ha - ver `reuseRows`.
    const scope = reuse ?? new HistoryRowScope();
    scope.identity = entry.date;
    scope.day = dayKeyOf(entry.date);
    scope.label = formatDate(entry.date);
    scope.sortValue = instantOf(entry.date);
    scope.cells = fields
        .slice(1)
        .map((column) => column.field === 'origem'
        ? buildSourceCell(entry.values ?? [], update, cellOf(reuse, 'origem'))
        : buildCell(entry.values ?? [], Number(column.field), update, undefined, undefined, cellOf(reuse, Number(column.field))));
    scope.onOpen = onOpen;
    scope.update = update;
    return scope;
}
/**
 * Uma linha da tabela de leituras - hoje uma HORA, e nao mais um instante.
 *
 * @param onOpen chamado com o tipo da celula clicada, para abrir o detalhamento
 * @param fields as colunas, na ordem em que a tabela as mostra - a primeira e a hora
 */
export function buildReadingRow(entry, update, onOpen, fields = READING_FIELDS, reuse) {
    // A linha que ja esta na tela, quando ha - ver `reuseRows`.
    const scope = reuse ?? new HistoryRowScope();
    scope.identity = entry.date;
    // A linha de LEITURA nao abre dia: reaproveitada de uma linha de dia de mesma
    // identidade (a hora 00:00), ela nao pode herdar aquela acao.
    scope.day = '';
    scope.onOpen = Scope.ASYNC_ACTION;
    scope.label = formatDateTime(entry.date);
    scope.sortValue = instantOf(entry.date);
    scope.cells = fields
        .slice(1)
        .map((column) => buildCell(entry.values ?? [], Number(column.field), update, entry.date, onOpen, cellOf(reuse, Number(column.field))));
    scope.update = update;
    return scope;
}
/** A celula de mesma coluna da linha reaproveitada, quando ha. */
function cellOf(row, identity) {
    return row?.cells.find((cell) => cell.identity === identity);
}
/**
 * Uma celula reaproveitada volta ao vazio: quem a monta so preenche "quando ha", e sem
 * isto ficaria o valor da carga anterior.
 */
function blank(scope) {
    scope.source = false;
    scope.sourceIcon = undefined;
    scope.value = '-';
    scope.statusName = undefined;
    scope.statusColor = undefined;
    scope.range = undefined;
    scope.single = false;
    scope.sortValue = null;
    scope.onSelect = Scope.ASYNC_ACTION;
}
/** A celula de "Dispositivo de Origem": o aparelho que mais mediu naquele dia. */
function buildSourceCell(values, update, reuse) {
    const scope = reuse ?? new HistoryCellScope();
    blank(scope);
    scope.identity = 'origem';
    scope.field = 'origem';
    scope.source = true;
    scope.update = update;
    const source = mostUsedSource(values);
    scope.value = source?.title || '-';
    scope.sourceIcon = source?.icon;
    return scope;
}
function buildCell(values, typeId, update, hour, onOpen, reuse) {
    const scope = reuse ?? new HistoryCellScope();
    blank(scope);
    scope.identity = typeId;
    scope.field = String(typeId);
    scope.update = update;
    const measurement = values.find((item) => item.measurementTypeId === typeId);
    if (!measurement) {
        return scope;
    }
    scope.value = formatMeasurement(typeId, measurement.value);
    scope.sortValue = measurement.value;
    scope.single = (measurement.readings ?? 1) <= 1;
    /*
     * A amplitude so aparece quando as pontas DIVERGEM.
     *
     * O limiar aqui e tipografico, e nao clinico: mostrar `70-70` gastaria uma linha
     * para nao dizer nada. Errar nele custa poluicao visual, e nao um sinal de saude
     * errado - que e a razao de o criterio de dispersao ficar para depois, com dado
     * real a vista.
     */
    if (measurement.min != null && measurement.max != null && measurement.min !== measurement.max) {
        scope.range = `${formatMeasurement(typeId, measurement.min)}-${formatMeasurement(typeId, measurement.max)}`;
    }
    if (measurementTypeOf(typeId)?.hasStatus) {
        scope.statusName = STATUS_LABELS[measurement.statusLevel];
        scope.statusColor = STATUS_TONES[measurement.statusLevel];
    }
    if (onOpen && hour) {
        scope.onSelect = async () => {
            await onOpen(typeId, hour);
        };
    }
    return scope;
}
/**
 * Ordena as linhas como a referencia ordena.
 *
 * Por NUMERO quando a coluna e uma medicao ou uma data, e nao por texto - "9" nao
 * vem depois de "10", e "01/02" nao vem depois de "31/01".
 */
/**
 * O valor pelo qual uma linha do historico se ordena, por campo.
 *
 * A celula e achada pelo CAMPO, e nao pela posicao: a posicao e a que a pessoa
 * escolheu arrastando, e muda. Linha sem a celula (a coluna nao existe naquela tabela)
 * nao tem valor.
 */
export function historySortValue(row, field) {
    if (field === 'date')
        return row.sortValue;
    const cell = row.cells.find((item) => item.field === field);
    if (!cell)
        return null;
    if (cell.source)
        return cell.value === '-' ? null : cell.value;
    return sortableNumber(cell.sortValue);
}
/**
 * O modelo da tabela do historico, com o universo de colunas ja declarado - as da
 * lista de dias e as das leituras, que a mesma tabela alterna. A presa e a primeira,
 * a data ou a hora.
 */
export function historyTableModel(name, account, legacy) {
    const model = new TableModel({ name, account, pinStart: 1, legacy });
    model.setColumns(HISTORY_SORT_FIELDS.map((field) => ({ field, sortValue: (row) => historySortValue(row, field) })));
    return model;
}
/**
 * O valor que entra na ordenacao.
 *
 * Zero e um valor, e nao ausencia. A referencia passava `a.steps?.value || null`
 * ao comparador, e `0 || null` e `null` - uma medicao de valor zero ordenava como
 * medicao ausente. Consertado nos dois paineis em 2026-09-12 (`??` no lugar de
 * `||`, em `tablePatients`, `tableHistory` e `tableHistoryDays`).
 */
function sortableNumber(value) {
    return value ?? null;
}
function mostUsedSource(values) {
    if (values.length === 0) {
        return undefined;
    }
    const count = new Map();
    for (const value of values) {
        count.set(value.measurementSourceId, (count.get(value.measurementSourceId) ?? 0) + 1);
    }
    let chosen = -1;
    let best = -1;
    for (const [id, total] of count) {
        if (total > best) {
            best = total;
            chosen = id;
        }
    }
    return MEASUREMENT_SOURCES.find((item) => item.id === chosen);
}
/** O instante em milissegundos, para a ordenacao ser por tempo e nao por texto. */
function instantOf(iso) {
    const value = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`).getTime();
    return Number.isNaN(value) ? 0 : value;
}
/** `yyyy-MM-dd`, que e a chave com que o dia entra no endereco. */
function dayKeyOf(iso) {
    return iso.slice(0, 10);
}
function formatDate(iso) {
    const [year, month, day] = iso.slice(0, 10).split('-');
    return `${day}/${month}/${year}`;
}
/**
 * `dd/MM/yyyy - HH:mm`, que e o que a coluna "Hora Monitoramento" mostra.
 *
 * Data E hora, apesar de a tabela ser de um dia so - e o `formatDateTimeBr` que a
 * referencia usa ali. Por fatia de string, e nao por `Date`: a data vem sem fuso.
 */
export function formatDateTime(iso) {
    if (!iso.includes('T')) {
        return formatDate(iso);
    }
    return `${formatDate(iso)} - ${iso.slice(11, 16)}`;
}
//# sourceMappingURL=history-table.js.map